import React, { FC, useCallback, useState, useMemo, useRef, ChangeEvent, useEffect } from 'react'
import { Button, Heading, Icon, Modal, Text, Checkbox, Input } from '@afterpaytouch/core'
import { useConsumerSizes } from '../../../../../utils/responsive'
import { Section75DisputeModalSteps, useSection75DisputeModalContext } from '../../Section75DisputeModalContext'
import { checkAmount } from '../../../utils'
import { useSelectedItemsTotal } from '../../hooks'
import { MAX_FILE_SIZE } from '../../../../../model'
import { useTranslation } from 'next-i18next'

const I18N_NAMESPACE = ['dispute']

export const DisputeAmount: FC = () => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const { textSize } = useConsumerSizes()
  const {
    setStep,
    order,
    useCustomAmount,
    setUseCustomAmount,
    customAmount,
    setCustomAmount,
    amountReason,
    setAmountReason,
    selectedItems,
    setSelectedItems,
    currentItemIndex,
    files,
    setFiles,
  } = useSection75DisputeModalContext()

  const [error, setError] = useState<string>('')

  const selectedItemsTotal = useSelectedItemsTotal(selectedItems)
  const uploadRef = useRef<HTMLInputElement>(null)

  const backHandler = useCallback(() => {
    setStep(Section75DisputeModalSteps.ChooseItems)
  }, [setStep])

  const handleNext = useCallback(() => {
    // Validation for custom amount
    if (useCustomAmount) {
      if (customAmount <= 0) {
        setError('Amount must be greater than 0')
        return
      }

      if (!checkAmount(customAmount)) {
        setError('Please enter a valid amount')
        return
      }

      // No maximum validation for custom amount in S75 flow - can exceed original amount
    }

    setStep(Section75DisputeModalSteps.OtherDocs)
  }, [setStep, useCustomAmount, customAmount])

  const handleCustomAmountChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setUseCustomAmount(event.target.checked)
      setError('')

      // When toggling custom amount, update the current item's amount
      if (currentItemIndex !== null && selectedItems[currentItemIndex]) {
        const updatedItems = [...selectedItems]
        const currentItem = updatedItems[currentItemIndex]

        if (event.target.checked) {
          // If enabling custom amount, set to the current custom amount value
          updatedItems[currentItemIndex] = {
            ...currentItem,
            amount: {
              ...currentItem.amount,
              amount: customAmount.toString(),
            },
          }
        } else {
          // If disabling custom amount, set back to default amount from order
          const defaultAmount = order?.orderSummary?.totalAfterRefund?.amount || '0'
          updatedItems[currentItemIndex] = {
            ...currentItem,
            amount: {
              ...currentItem.amount,
              amount: defaultAmount,
            },
          }
        }

        setSelectedItems(updatedItems)
      }
    },
    [setUseCustomAmount, currentItemIndex, selectedItems, setSelectedItems, customAmount, order]
  )

  const handleCustomAmountValueChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const amount = Number(event.target.value.trim())
      setCustomAmount(amount)
      setError('')

      // Update the current item's amount with the custom amount or default amount
      if (currentItemIndex !== null && selectedItems[currentItemIndex]) {
        const updatedItems = [...selectedItems]
        const currentItem = updatedItems[currentItemIndex]

        // Use custom amount if > 0, otherwise use default amount
        const amountToUse = amount > 0 ? amount.toString() : order?.orderSummary?.totalAfterRefund?.amount || '0'

        updatedItems[currentItemIndex] = {
          ...currentItem,
          amount: {
            ...currentItem.amount,
            amount: amountToUse,
          },
        }

        setSelectedItems(updatedItems)
      }
    },
    [setCustomAmount, currentItemIndex, selectedItems, setSelectedItems, order]
  )

  const handleAmountReasonChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const reason = event.target.value
      setAmountReason(reason)
    },
    [setAmountReason]
  )

  const addFileButtonClickHandler = useCallback(() => {
    uploadRef.current?.click()
  }, [])

  const fileChangedHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const fileList = Array.from(event.target.files ?? [])

      const fileOutOfSizeList = fileList.filter((file: File) => file.size > MAX_FILE_SIZE)
      if (fileOutOfSizeList.length > 0) {
        setError('File size exceeds maximum allowed size')
        return
      }

      // Add files to global files array
      setFiles([...files, ...fileList])

      // Add file names to the current item's fileKeys if we have a current item
      if (currentItemIndex !== null && selectedItems[currentItemIndex]) {
        const updatedItems = [...selectedItems]
        const currentItem = updatedItems[currentItemIndex]
        const newFileNames = fileList.map((file) => file.name)

        updatedItems[currentItemIndex] = {
          ...currentItem,
          fileKeys: [...(currentItem.fileKeys || []), ...newFileNames],
        }

        setSelectedItems(updatedItems)
      }

      setError('')
      event.target.value = ''
    },
    [files, setFiles, currentItemIndex, selectedItems, setSelectedItems]
  )

  // Set default amount to current item when component loads or item changes
  useEffect(() => {
    if (currentItemIndex !== null && selectedItems[currentItemIndex] && order?.orderSummary?.totalAfterRefund?.amount) {
      const currentItem = selectedItems[currentItemIndex]
      const defaultAmount = order.orderSummary.totalAfterRefund.amount

      // Only set default amount if the item doesn't already have an amount or if not using custom amount
      if (!currentItem.amount?.amount || (!useCustomAmount && currentItem.amount.amount === '0')) {
        const updatedItems = [...selectedItems]
        updatedItems[currentItemIndex] = {
          ...currentItem,
          amount: {
            ...currentItem.amount,
            amount: defaultAmount,
          },
        }
        setSelectedItems(updatedItems)
      }
    }
  }, [currentItemIndex, selectedItems, order, useCustomAmount, setSelectedItems])

  // Get the dispute amount using the selected items total hook
  const disputeAmount = useMemo(() => {
    const symbol = order?.orderSummary?.orderTotal?.symbol ?? '£'
    // Use the selected items total from the hook - this is the primary source
    const amount = selectedItemsTotal > 0 ? selectedItemsTotal.toFixed(2) : '0.00'

    return `${symbol}${amount}`
  }, [order, selectedItemsTotal, selectedItems])

  return (
    <>
      <Modal.Header divider={true}>
        <div className='absolute z-10 pt-1'>
          <Button.Link size='Small' onClick={backHandler}>
            <Icon.CaretLeft size='Small' />
          </Button.Link>
        </div>
        <div className='text-center'>
          <Heading size='L'>{t('dispute:section75DisputeModal:disputeAmount:heading')}</Heading>
        </div>
      </Modal.Header>
      <Modal.Content>
        <div className='mb-6'>
          <Text size={textSize} className='mb-4'>
            {t('dispute:section75DisputeModal:disputeAmount:amount')}
          </Text>
          <div className='mb-6'>
            <Text size={'XXXXL'} bold className='mb-4'>
              {disputeAmount}
            </Text>
          </div>
        </div>

        <div className='mb-6'>
          <Checkbox
            id='custom-amount'
            name='custom-amount'
            onChange={handleCustomAmountChange}
            label={t('dispute:section75DisputeModal:disputeAmount:notSameAmount')}
            checked={useCustomAmount}
          />
        </div>

        {useCustomAmount && (
          <div className='mb-6'>
            <Text size={textSize} className='mb-4'>
              {t('dispute:section75DisputeModal:disputeAmount:customAmountQuestion')}
            </Text>
            <div className='mb-4 flex items-stretch'>
              <Input.Number
                label={t('dispute:section75DisputeModal:disputeAmount:customAmountLabel')}
                icon='Coins'
                iconPosition='Left'
                fullWidth={true}
                value={customAmount.toString()}
                onChange={handleCustomAmountValueChange}
                placeholder='£0.00'
              />
              <div className='p-md flex items-center justify-center'>
                <Text>{order?.orderSummary?.orderTotal?.currency}</Text>
              </div>
            </div>
            <Input.TextArea
              rows={4}
              fullWidth={true}
              label={t('dispute:section75DisputeModal:disputeAmount:reasonLabel')}
              value={amountReason}
              onChange={handleAmountReasonChange}
            />
          </div>
        )}

        {/* File upload section for custom amount */}
        {useCustomAmount && (
          <div className='mb-6'>
            <Text size={textSize} bold>
              {t('dispute:section75DisputeModal:disputeAmount:supportingDocs')}
            </Text>
            <Text className='mt-1 text-gray-600'>{t('dispute:section75DisputeModal:disputeAmount:supportingDocsDesc')}</Text>

            <div className='mt-3'>
              <Button.Ghost size='Regular' padding='Fluid' onClick={addFileButtonClickHandler}>
                {t('dispute:section75DisputeModal:disputeAmount:chooseFiles')}
              </Button.Ghost>
              <input type='file' className='hidden' multiple onChange={fileChangedHandler} accept='image/*,.pdf,.doc,.docx' ref={uploadRef} />
            </div>

            {/* Display uploaded files */}
            {files.length > 0 && (
              <div className='mt-3'>
                <Text className='text-gray-600'>{t('dispute:section75DisputeModal:disputeAmount:uploadedFiles')}</Text>
                <ul className='mt-1'>
                  {files.map((file, index) => (
                    <li key={index} className='text-sm text-gray-700'>
                      <Text className='text-gray-600'>{file.name}</Text>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className='mb-6'>
            <Text size={textSize} color='Fire'>
              {error}
            </Text>
          </div>
        )}

        <div className='mt-auto'>
          <Button padding='Fluid' size='Large' onClick={handleNext}>
            {t('dispute:section75DisputeModal:disputeAmount:next')}
          </Button>
        </div>
      </Modal.Content>
    </>
  )
}
